import React, { useEffect, useRef, useState } from "react";

import { CopyButton } from "@/shared/components/markdown/CodeBlock";
import { cn } from "@/shared/lib/cn";

/* A call's arguments or result, opened under its row (`StepRows`): a label and the shared copy
   button, then the text in mono, JSON indented and coloured on the --syntax-* tokens. It wraps
   instead of scrolling sideways; a long one fades out under "Show more". While the model is
   still writing it, it carries a caret and is never capped: the newest words stay in view. */

/** Taller than this, a block fades out under "Show more". */
const CAP_PX = 196;
/** Past this many characters the rest is cut: a result can be a whole page. */
const TEXT_CAP = 20000;

export default function RunCode({ label, text, live = false }: { label: string; text: string; live?: boolean }) {
  const shown = readable(text);
  const block = useRef<HTMLPreElement>(null);
  const [tall, setTall] = useState(false);
  const [full, setFull] = useState(false);
  // measured again whenever the block's width changes what it wraps to
  useEffect(() => {
    const pre = block.current;
    if (!pre || live) return;
    const measure = () => setTall(pre.scrollHeight > CAP_PX + 8);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(pre);
    return () => observer.disconnect();
  }, [shown.text, live]);
  const capped = tall && !full && !live;
  return (
    <div className="grid grid-cols-1 gap-1.5 min-w-0">
      <div className="flex items-center justify-between gap-2 min-h-6">
        <span className="text-[12px] font-medium text-[var(--text-tertiary)]">{label}</span>
        {!live && <CopyButton text={text} label={`Copy ${label.toLowerCase()}`} />}
      </div>
      <pre
        ref={block}
        style={{ maxHeight: full || live ? undefined : CAP_PX }}
        className={cn(
          "relative m-0 px-3 py-2.5 overflow-hidden rounded-[10px] bg-[var(--surface-sunk)] ring-1 ring-inset ring-[var(--hairline)] font-mono text-[12px] leading-[1.6] text-[var(--text-tertiary)] whitespace-pre-wrap [overflow-wrap:anywhere]",
          capped && "after:absolute after:inset-x-px after:bottom-px after:h-14 after:rounded-b-[9px] after:bg-gradient-to-b after:from-transparent after:to-[var(--surface-sunk)]",
        )}
      >
        <code>{shown.json ? highlighted(shown.text) : shown.text}</code>
        {live && <span className="stream-cursor text-[var(--text-primary)]">▍</span>}
      </pre>
      {tall && !live && (
        <button type="button" onClick={() => setFull(!full)} className="justify-self-start -ml-1 px-1 py-0.5 rounded-[5px] text-[12.5px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
          {full ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

// a terminal's colour and cursor codes, which a command's output carries and a page can't draw
const TERMINAL_CODES = /\u001b\[[0-?]*[ -/]*[@-~]/g;

/** Text as it reads best: JSON indented, anything else as it came less terminal codes, a very
    long one cut. Copy keeps it as it came. */
function readable(text: string): { text: string; json: boolean } {
  let out = text.replace(TERMINAL_CODES, "");
  let json = false;
  try {
    const parsed: unknown = JSON.parse(text);
    if (parsed !== null && typeof parsed === "object") {
      out = JSON.stringify(parsed, null, 2);
      json = true;
    }
  } catch {
    // not JSON: shown as it came
  }
  return { text: out.length > TEXT_CAP ? `${out.slice(0, TEXT_CAP)}\n…` : out, json };
}

// a key with its colon, a string, a literal or a number, in indented JSON
const TOKEN = /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false|null)\b|-?\b\d+(?:\.\d+)?(?:[eE][+-]?\d+)?\b/g;

/** Indented JSON with its keys in ink and its strings, numbers and literals coloured. */
function highlighted(json: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let at = 0;
  for (const match of json.matchAll(TOKEN)) {
    const [token, string, colon, literal] = match;
    if (match.index > at) out.push(json.slice(at, match.index));
    const color = string ? (colon ? "text-[var(--text-primary)]" : "text-[var(--syntax-string)]") : literal ? "text-[var(--syntax-keyword)]" : "text-[var(--syntax-number)]";
    out.push(<span key={match.index} className={color}>{string ?? token}</span>);
    if (colon) out.push(colon);
    at = match.index + token.length;
  }
  out.push(json.slice(at));
  return out;
}
